import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Screen } from '@/components/Screen'
import { Kicker } from '@/components/Kicker'
import { SkeletonList } from '@/components/Skeleton'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import { useFocusedPerson } from '@/hooks/useFocusedPerson'
import type { EventRow } from '@/lib/types'

/**
 * TrendsScreen — 14-day feed-intervals + sleep-hours-per-day + regression alert.
 * SVG charts, no external chart lib. Aggregates client-side from events query.
 */

const WINDOW_DAYS = 14
const MS_DAY = 24 * 60 * 60 * 1000

export function TrendsScreen() {
  const familyId = useAppStore((s) => s.familyId)
  const person = useFocusedPerson()

  const q = useQuery({
    enabled: !!familyId && !!person,
    queryKey: ['trends', familyId, person?.id],
    queryFn: async () => {
      const since = new Date(Date.now() - WINDOW_DAYS * MS_DAY).toISOString()
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('family_id', familyId!)
        .eq('person_id', person!.id)
        .is('supersedes_event_id', null)
        .in('event_type', ['early_care.feeding.recorded', 'early_care.sleep.started', 'early_care.sleep.ended'])
        .gte('occurred_at', since)
        .order('occurred_at', { ascending: true })
        .limit(2000)
      if (error) throw error
      return (data ?? []) as EventRow[]
    },
  })

  // Pump events aren't attached to the baby — they're on the pumping adult. Query
  // family-wide so we can render supply trend for whoever pumped.
  const pumpQ = useQuery({
    enabled: !!familyId,
    queryKey: ['pump-trends', familyId],
    queryFn: async () => {
      const since = new Date(Date.now() - WINDOW_DAYS * MS_DAY).toISOString()
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('family_id', familyId!)
        .eq('event_type', 'early_care.pump.recorded')
        .is('supersedes_event_id', null)
        .gte('occurred_at', since)
        .order('occurred_at', { ascending: true })
        .limit(500)
      if (error) throw error
      return (data ?? []) as EventRow[]
    },
  })

  const feedIntervals = useMemo(() => aggregateFeedIntervals(q.data ?? []), [q.data])
  const sleepDailyHours = useMemo(() => aggregateSleepHours(q.data ?? []), [q.data])
  const sleepRegression = useMemo(() => detectSleepRegression(sleepDailyHours), [sleepDailyHours])
  const clusters = useMemo(() => detectClusters(q.data ?? []), [q.data])
  const pumpDaily = useMemo(() => aggregatePumpVolumes(pumpQ.data ?? []), [pumpQ.data])
  const hasPumps = pumpDaily.some((d) => d.value > 0)

  if (!person) return <Screen title="Trends"><div className="p-6 text-center text-text-tertiary">Pick a person to see their trends.</div></Screen>

  return (
    <Screen title={`${person.display_name} · Trends`}>
      <div className="px-4 pt-3 pb-6 space-y-4">
        {q.isLoading && <SkeletonList rows={2} />}

        {sleepRegression && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="glass-tile p-4 relative overflow-hidden bloom-coral"
          >
            <div className="relative z-10">
              <Kicker>Sleep regression detected</Kicker>
              <div className="mt-2 text-body text-text-primary">
                {sleepRegression.msg}
              </div>
              <div className="mt-2 text-caption text-text-tertiary">
                Not a diagnosis — just a pattern shift. Talk to your pediatrician if it persists past 2 weeks.
              </div>
            </div>
          </motion.div>
        )}

        <BarChartCard
          title="Feeds per day"
          subtitle="Bars = number of feeds each day"
          bars={feedIntervals.perDay}
          unit=""
        />

        <BarChartCard
          title="Sleep hours per day"
          subtitle="Bars = total sleep hours per day"
          bars={sleepDailyHours}
          unit="h"
        />

        {clusters.length > 0 && (
          <div className="glass-tile p-4 relative overflow-hidden bloom-mint">
            <div className="relative z-10">
              <Kicker>Cluster feeds detected</Kicker>
              <div className="mt-2 text-body text-text-primary">
                {clusters.length} cluster{clusters.length === 1 ? '' : 's'} in the last 14 days
                {clusters.length > 0 && ` — most recent ${new Date(clusters[clusters.length - 1].startAt).toLocaleDateString()}`}
              </div>
              <div className="mt-1 text-caption text-text-tertiary">
                3+ feeds within 2 hours. Normal for growth spurts + evenings.
              </div>
            </div>
          </div>
        )}

        {hasPumps && (
          <BarChartCard
            title="Pump volume per day"
            subtitle="Total mL pumped each day"
            bars={pumpDaily}
            unit="mL"
          />
        )}

        <div className="glass-tile p-4">
          <Kicker>Feed interval trend</Kicker>
          <div className="mt-2 text-body text-text-primary">
            Median gap between feeds this week:
            <span className="ml-2 font-display text-title font-medium">
              {feedIntervals.medianHours != null ? `${feedIntervals.medianHours.toFixed(1)}h` : '—'}
            </span>
          </div>
          <div className="mt-1 text-caption text-text-tertiary">
            {feedIntervals.medianHours != null && feedIntervals.medianHours < 2 && 'Cluster feeding pattern.'}
            {feedIntervals.medianHours != null && feedIntervals.medianHours >= 2 && feedIntervals.medianHours < 4 && 'Newborn pace — normal.'}
            {feedIntervals.medianHours != null && feedIntervals.medianHours >= 4 && 'Longer stretches — established feeding rhythm.'}
          </div>
        </div>
      </div>
    </Screen>
  )
}

// ============================================================
// Aggregators
// ============================================================

interface DailyBar { day: string; value: number }

function aggregateFeedIntervals(events: EventRow[]): { perDay: DailyBar[]; medianHours: number | null } {
  const feeds = events.filter((e) => e.event_type === 'early_care.feeding.recorded').sort((a, b) =>
    a.occurred_at.localeCompare(b.occurred_at))
  const perDay: DailyBar[] = buildDayBuckets()
  const byIso = new Map(perDay.map((d) => [d.day, d]))
  const gaps: number[] = []
  for (let i = 0; i < feeds.length; i++) {
    const iso = feeds[i].occurred_at.slice(0, 10)
    const bucket = byIso.get(iso)
    if (bucket) bucket.value++
    if (i > 0) {
      const gap = (new Date(feeds[i].occurred_at).getTime() - new Date(feeds[i - 1].occurred_at).getTime()) / 3_600_000
      if (gap > 0 && gap < 12) gaps.push(gap) // sanity: ignore multi-day gaps from data pauses
    }
  }
  const medianHours = gaps.length > 0 ? median(gaps) : null
  return { perDay, medianHours }
}

function aggregateSleepHours(events: EventRow[]): DailyBar[] {
  const perDay: DailyBar[] = buildDayBuckets()
  const byIso = new Map(perDay.map((d) => [d.day, d]))
  // Pair sleep.started with next sleep.ended per person; count total hours per day
  const relevant = events.filter((e) => e.event_type === 'early_care.sleep.started' || e.event_type === 'early_care.sleep.ended')
  const stack: EventRow[] = []
  for (const e of relevant) {
    if (e.event_type === 'early_care.sleep.started') {
      stack.push(e)
    } else if (e.event_type === 'early_care.sleep.ended' && stack.length > 0) {
      const start = stack.pop()!
      const from = new Date(start.occurred_at).getTime()
      const to = new Date(e.occurred_at).getTime()
      if (to <= from) continue
      // Distribute hours over the days the nap spanned
      let cursor = from
      while (cursor < to) {
        const dayEnd = endOfDayMs(cursor)
        const chunkEnd = Math.min(to, dayEnd)
        const iso = new Date(cursor).toISOString().slice(0, 10)
        const bucket = byIso.get(iso)
        if (bucket) bucket.value += (chunkEnd - cursor) / 3_600_000
        cursor = chunkEnd + 1
      }
    }
  }
  // Round to 1 decimal for display
  for (const b of perDay) b.value = Math.round(b.value * 10) / 10
  return perDay
}

function detectSleepRegression(daily: DailyBar[]): { msg: string } | null {
  if (daily.length < 7) return null
  // Compare last 3 days median vs prior 4 days median
  const recent = daily.slice(-3).map((d) => d.value).filter((v) => v > 0)
  const prior = daily.slice(-7, -3).map((d) => d.value).filter((v) => v > 0)
  if (recent.length < 2 || prior.length < 2) return null
  const rMed = median(recent)
  const pMed = median(prior)
  if (pMed === 0) return null
  const drop = (pMed - rMed) / pMed
  if (drop >= 0.25) {
    return { msg: `Median daily sleep dropped ${Math.round(drop * 100)}% — from ${pMed.toFixed(1)}h → ${rMed.toFixed(1)}h over the past 3 days.` }
  }
  return null
}

function buildDayBuckets(): DailyBar[] {
  const out: DailyBar[] = []
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  for (let i = WINDOW_DAYS - 1; i >= 0; i--) {
    const d = new Date(now.getTime() - i * MS_DAY)
    out.push({ day: d.toISOString().slice(0, 10), value: 0 })
  }
  return out
}

function endOfDayMs(ms: number): number {
  const d = new Date(ms)
  d.setHours(23, 59, 59, 999)
  return d.getTime()
}

function detectClusters(events: EventRow[]): Array<{ startAt: string; count: number }> {
  const feeds = events.filter((e) => e.event_type === 'early_care.feeding.recorded').sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))
  const out: Array<{ startAt: string; count: number }> = []
  const WINDOW_MS = 2 * 60 * 60 * 1000
  for (let i = 0; i < feeds.length - 2; i++) {
    const start = new Date(feeds[i].occurred_at).getTime()
    let j = i + 1
    let count = 1
    while (j < feeds.length && new Date(feeds[j].occurred_at).getTime() - start <= WINDOW_MS) {
      count++
      j++
    }
    if (count >= 3) {
      out.push({ startAt: feeds[i].occurred_at, count })
      i = j - 1  // skip past this cluster to avoid re-counting
    }
  }
  return out
}

function aggregatePumpVolumes(events: EventRow[]): DailyBar[] {
  const perDay = buildDayBuckets()
  const byIso = new Map(perDay.map((d) => [d.day, d]))
  for (const e of events) {
    const p = e.payload as Record<string, unknown> | null
    const total = typeof p?.volume_ml_total === 'number' ? (p.volume_ml_total as number) :
      ((typeof p?.volume_ml_left === 'number' ? (p.volume_ml_left as number) : 0) +
       (typeof p?.volume_ml_right === 'number' ? (p.volume_ml_right as number) : 0))
    const iso = e.occurred_at.slice(0, 10)
    const bucket = byIso.get(iso)
    if (bucket) bucket.value += total
  }
  for (const b of perDay) b.value = Math.round(b.value)
  return perDay
}

function median(xs: number[]): number {
  const s = xs.slice().sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid]
}

// ============================================================
// SVG bar chart
// ============================================================

function BarChartCard({ title, subtitle, bars, unit }: { title: string; subtitle: string; bars: DailyBar[]; unit: string }) {
  const maxV = Math.max(1, ...bars.map((b) => b.value))
  const W = 340, H = 180, padL = 28, padR = 8, padT = 14, padB = 26
  const plotW = W - padL - padR
  const plotH = H - padT - padB
  const barW = plotW / bars.length
  return (
    <div className="glass-tile p-4">
      <Kicker>{title}</Kicker>
      <div className="mt-1 text-caption text-text-tertiary">{subtitle}</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="mt-3 block w-full h-auto">
        {/* Y-axis gridline */}
        {[0, 0.5, 1].map((frac, i) => (
          <g key={i}>
            <line
              x1={padL} x2={W - padR}
              y1={padT + plotH * (1 - frac)}
              y2={padT + plotH * (1 - frac)}
              stroke="currentColor" strokeOpacity={0.06}
            />
            <text
              x={padL - 4} y={padT + plotH * (1 - frac) + 3}
              fontSize="9" textAnchor="end" fill="currentColor" fillOpacity={0.6}
            >
              {(maxV * frac).toFixed(0)}{unit}
            </text>
          </g>
        ))}
        {bars.map((b, i) => {
          const h = (b.value / maxV) * plotH
          const x = padL + i * barW + 2
          const y = padT + plotH - h
          const w = Math.max(1, barW - 4)
          return (
            <g key={i}>
              <motion.rect
                initial={{ height: 0, y: padT + plotH }}
                animate={{ height: h, y }}
                transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1], delay: i * 0.02 }}
                x={x} width={w} rx={3}
                fill="#FF7A2D" opacity={b.value > 0 ? 1 : 0.15}
              />
              {i % 2 === 0 && (
                <text
                  x={x + w / 2} y={H - padB + 12}
                  fontSize="8" textAnchor="middle" fill="currentColor" fillOpacity={0.55}
                >
                  {b.day.slice(5)}
                </text>
              )}
            </g>
          )
        })}
      </svg>
    </div>
  )
}
